import { ButtonV2, Combobox, ComboboxActions, OdinIcon } from '@myosh/odin-components';
import { type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useBowtieHazardMueMaterialUnwantedEventsJobPostMutation } from '../../api/enhanced/enhanced-bowtie-api';
import type { BowtieHazardMueMaterialUnwantedEventsPostApiResponse } from '../../api/generated/bowtie-api';
import { useGetAIBackgroundJobResult } from '../../hooks/use-get-ai-background-job-result';
import Accordion from './accordion.component';
import AddItemDialog from './add-item-dialog.component';
import Dialog, { type DialogRef } from './dialog';

interface BowtieWizardProps {
  ref?: Ref<BowtieWizardRef>;
}

export interface BowtieWizardRef {
  open: () => void;
  close: () => void;
}

interface MueItem {
  text: string;
  value: string;
}

interface WizardDropDownItem {
  text: string;
  value: string;
  mue: Array<MueItem>;
}

interface BowtieWizardState {
  canGenerateMUEs: boolean;
  loading: boolean;
  hazardMuePair: Array<WizardDropDownItem>;
  selectedMUE?: MueItem;
  selectedHazard?: WizardDropDownItem;
  isAddHazard: boolean;
  /** Value of a manually added hazard whose unwanted events job is still in flight. */
  pendingHazard?: string;
}

const initialState: BowtieWizardState = {
  canGenerateMUEs: false,
  loading: false,
  hazardMuePair: [],
  selectedMUE: undefined,
  selectedHazard: undefined,
  isAddHazard: false,
  pendingHazard: undefined,
};

const BowtieWizard = ({ ref }: BowtieWizardProps) => {
  const [state, setState] = useState<BowtieWizardState>(initialState);
  const [jobId, setJobId] = useState<string>();
  const { canGenerateMUEs, loading, hazardMuePair, selectedMUE, selectedHazard, isAddHazard } = state;

  const dialogRef = useRef<DialogRef>(null);
  const addDataDialogRef = useRef<DialogRef>(null);
  const termInputRef = useRef<HTMLInputElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const newItemInputRef = useRef<HTMLInputElement>(null);
  const errorTextRef = useRef<HTMLParagraphElement>(null);

  const [startMaterialUnwantedEventsJob] = useBowtieHazardMueMaterialUnwantedEventsJobPostMutation();

  const {
    data: backgroundJobResult,
    error: backgroundJobError,
    loading: backgroundJobLoading,
  } = useGetAIBackgroundJobResult({
    jobId: jobId!,
    setJobId: setJobId,
  });

  const isLoading = loading || backgroundJobLoading;

  useEffect(() => {
    if (backgroundJobResult && !isLoading && !backgroundJobError) {
      setJobId(undefined);

      const data = backgroundJobResult as unknown as BowtieHazardMueMaterialUnwantedEventsPostApiResponse;
      if (data?.length > 0) {
        setState((prevState) => {
          if (prevState.pendingHazard) {
            const matched = data.find(({ hazard }) => hazard === prevState.pendingHazard) ?? data[0];
            const generatedMUE = mapMUE(matched.material_unwanted_event);
            const hazardMuePair = prevState.hazardMuePair.map((item) =>
              item.value === prevState.pendingHazard ? { ...item, mue: mergeMUE(item.mue, generatedMUE) } : item
            );

            return {
              ...prevState,
              loading: false,
              selectedMUE: undefined,
              hazardMuePair,
              selectedHazard: hazardMuePair.find((item) => item.value === prevState.pendingHazard),
              pendingHazard: undefined,
            };
          }

          return {
            ...prevState,
            loading: false,
            hazardMuePair: data.map(({ material_unwanted_event, hazard }) => ({
              text: hazard,
              value: hazard,
              mue: mapMUE(material_unwanted_event),
            })),
          };
        });
      } else {
        setState((prevState) => ({ ...prevState, loading: false, pendingHazard: undefined }));
      }
    } else if (backgroundJobError && !isLoading) {
      toast.error('Unable to generate hazards and unwanted events, please try again later.');
      setState((state) => ({ ...state, loading: false, pendingHazard: undefined }));
    }
    setJobId(undefined);
  }, [backgroundJobResult, backgroundJobError, isLoading]);

  useImperativeHandle(ref, () => ({
    open: () => dialogRef.current?.open(),
    close,
  }));

  const close = useCallback(() => dialogRef.current?.close(), []);

  const handleTermInputChange = () => {
    if (!termInputRef.current?.value) {
      setState(initialState);
    }
  };

  const handleGenerateMUEs = () => {
    if (termInputRef.current?.value && termInputRef.current.value.trim().length > 0) {
      setState((state) => ({
        ...state,
        canGenerateMUEs: true,
        loading: true,
        selectedMUE: undefined,
        selectedHazard: undefined,
        pendingHazard: undefined,
      }));

      startMaterialUnwantedEventsJob({
        hazardMueRequest: {
          term: termInputRef.current.value.trim(),
          extra_prompt: promptInputRef?.current?.value.trim(),
        },
      })
        .unwrap()
        .then((jobId) => {
          setJobId(jobId);
          setState((state) => ({ ...state, loading: false }));
        })
        .catch(() => {
          setJobId(undefined);
          toast.error('Unable to generate hazards and unwanted events, please try again later.');
          setState((state) => ({ ...state, loading: false }));
        });
    }
  };

  const handleMUESelectionChange = (materialUnwantedEvent: MueItem | null) => {
    if (materialUnwantedEvent) {
      setState((state) => ({ ...state, selectedMUE: materialUnwantedEvent }));
    }
  };

  const handleHazardSelectionChange = (hazard: WizardDropDownItem | null) => {
    if (hazard) {
      setState((state) => ({ ...state, selectedMUE: undefined, selectedHazard: hazard }));
    }
  };

  const handleGenerateBowtieClick = () => {
    if (
      termInputRef.current?.value &&
      termInputRef.current.value.trim().length > 0 &&
      selectedMUE?.value &&
      selectedHazard?.value
    ) {
      // open new page and load bowtie
      const params = new URLSearchParams({
        term: termInputRef.current.value.trim(),
        hazard: selectedHazard.value,
        mue: selectedMUE.value,
      });

      const promptValue = promptInputRef.current?.value ?? '';
      if (promptValue) {
        params.append('prompt', promptValue);
      }

      window.open(`${window.location.origin}${window.location.pathname}?${params}`);
    }
  };

  const handleSubmitAddItem = async () => {
    const inputValue = newItemInputRef.current?.value?.trim();

    if (inputValue && inputValue.length > 0) {
      if (inputValue.length > 100) {
        if (errorTextRef.current) {
          errorTextRef.current.style.display = 'block';
          errorTextRef.current.textContent = 'Value must not exceed 100 characters';
        }
      } else {
        if (errorTextRef.current) errorTextRef.current.style.display = 'none';

        if (isAddHazard) {
          setState((state) => {
            const existing = state.hazardMuePair.find((item) => item.value === inputValue);
            const newHazard: WizardDropDownItem = existing ?? { text: inputValue, value: inputValue, mue: [] };

            return {
              ...state,
              loading: true,
              hazardMuePair: existing ? state.hazardMuePair : [...state.hazardMuePair, newHazard],
              selectedHazard: newHazard,
              selectedMUE: undefined,
              pendingHazard: inputValue,
            };
          });

          if (newItemInputRef.current) newItemInputRef.current.value = '';

          startMaterialUnwantedEventsJob({
            hazardMueRequest: {
              term: termInputRef?.current?.value.trim() as string,
              extra_prompt: promptInputRef?.current?.value.trim(),
              hazard: inputValue,
            },
          })
            .unwrap()
            .then((jobId) => {
              setJobId(jobId);
              setState((state) => ({ ...state, loading: false }));
            })
            .catch(() => {
              toast.error('Unable to generate hazards and unwanted events, please try again later.');
              setJobId(undefined);
              setState((state) => ({ ...state, loading: false, pendingHazard: undefined }));
            });
        } else {
          const newMue = mergeMUE(selectedHazard?.mue ?? [], [{ text: inputValue, value: inputValue }]);
          const newSelectedHazard = selectedHazard ? { ...selectedHazard, mue: newMue } : undefined;

          const newHazardMuePair = hazardMuePair.map((item) =>
            item.value === selectedHazard?.value ? { ...item, mue: newMue } : item
          );

          setState((prevState) => ({
            ...prevState,
            hazardMuePair: newHazardMuePair,
            selectedHazard: newSelectedHazard,
            selectedMUE: { value: inputValue, text: inputValue },
          }));
          if (newItemInputRef.current) newItemInputRef.current.value = '';
        }

        addDataDialogRef.current?.close();
      }
    } else {
      if (errorTextRef.current) {
        errorTextRef.current.style.display = 'block';
        errorTextRef.current.textContent = 'Must enter a value';
      }
    }
  };

  const handleShowHazardAddItemDialog = () => {
    setState((state) => ({ ...state, isAddHazard: true }));
    addDataDialogRef.current?.open();
  };

  const handleShowMueAddItemDialog = () => {
    setState((state) => ({ ...state, isAddHazard: false }));
    addDataDialogRef.current?.open();
  };

  const sectionStyles = 'flex flex-1 flex-col items-center gap-4 rounded-md p-4';

  return (
    <>
      <Dialog
        data-cy="bowtie-wizard-dialog"
        className="m-auto w-150 rounded-md bg-popover p-4 text-popover-foreground outline-none"
        ref={dialogRef}
        onCancel={close}
      >
        <div className="mb-4 flex flex-row items-center justify-between">
          <div className="font-medium text-xl">Bowtie AI Wizard</div>
          <button type="button" data-cy="bowtie-wizard-close" title="Close" className="text-primary" onClick={close}>
            <OdinIcon icon="X" className="size-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4">
          <div className={sectionStyles}>
            <div className="w-full">
              <label htmlFor="term-input" className="flex self-start font-medium text-sm">
                Describe an area or term to generate Unwanted Events for
              </label>
              <input
                data-cy="bowtie-wizard-term-input"
                ref={termInputRef}
                id="term-input"
                name="term-input"
                onChange={handleTermInputChange}
                className="flex w-full flex-1 appearance-none border border-border px-2 py-1 text-sm focus:outline-none"
              />
            </div>
            <ButtonV2
              variant="outline"
              data-cy="bowtie-wizard-generate-button"
              className="min-w-[250px]"
              onClick={handleGenerateMUEs}
              disabled={canGenerateMUEs && isLoading}
            >
              {canGenerateMUEs && isLoading ? 'Generating...' : 'Generate Hazards and Events'}
            </ButtonV2>
          </div>
          <div className={sectionStyles}>
            <div className="w-full">
              <Combobox
                label="Hazards"
                placeholder="Select Hazard"
                items={hazardMuePair}
                getKey={(item) => item.value}
                getLabel={(item) => item.text}
                value={selectedHazard ?? null}
                onValueChange={handleHazardSelectionChange}
                disabled={!canGenerateMUEs || isLoading}
              >
                <ComboboxActions align="block-end">
                  <ButtonV2 variant="outline" onClick={handleShowHazardAddItemDialog}>
                    <OdinIcon icon="Plus" data-icon="inline-start" />
                    Add New
                  </ButtonV2>
                </ComboboxActions>
              </Combobox>
            </div>
            <div className="w-full">
              <Combobox
                label="Unwanted Events"
                placeholder="Select Unwanted Event"
                items={selectedHazard?.mue ?? []}
                getKey={(item) => item.value}
                getLabel={(item) => item.text}
                value={selectedMUE ?? null}
                onValueChange={handleMUESelectionChange}
                disabled={!canGenerateMUEs || isLoading || !selectedHazard?.value}
              >
                <ComboboxActions align="block-end">
                  <ButtonV2 variant="outline" onClick={handleShowMueAddItemDialog}>
                    <OdinIcon icon="Plus" data-icon="inline-start" />
                    Add New
                  </ButtonV2>
                </ComboboxActions>
              </Combobox>
            </div>
            <Accordion title="Advanced">
              <div className="w-full">
                <label htmlFor="custom-instructions" className="flex self-start font-medium text-sm">
                  Custom Instructions
                </label>
                <textarea
                  data-cy="bowtie-wizard-prompt-textarea"
                  ref={promptInputRef}
                  id="custom-instructions"
                  name="custom-instructions"
                  placeholder="Provide extra detail about your situation to generate a more relevant response"
                  className="flex w-full flex-1 resize-none appearance-none border border-border px-2 py-1 text-sm focus:outline-none"
                  cols={30}
                  rows={5}
                />
              </div>
            </Accordion>
            <ButtonV2
              data-cy="bowtie-wizard-submit-button"
              onClick={handleGenerateBowtieClick}
              disabled={!selectedMUE || !selectedHazard}
            >
              Generate Bowtie
            </ButtonV2>
            <p className="self-start text-warning text-xs">
              Warning: Data is sent to an external AI. Verify all AI results for accuracy.
            </p>
          </div>
        </div>
      </Dialog>
      <AddItemDialog
        ref={addDataDialogRef}
        errorTextRef={errorTextRef}
        inputRef={newItemInputRef}
        onSubmit={handleSubmitAddItem}
        disabled={isLoading}
      />
    </>
  );
};

const mapMUE = (mues: string[]) => mues.map((mue) => ({ text: mue, value: mue }));

/** Keeps any unwanted events already on the hazard (manually added ones) ahead of the generated set. */
const mergeMUE = (existing: Array<MueItem>, generated: Array<MueItem>) => [
  ...existing,
  ...generated.filter((mue) => !existing.some((item) => item.value === mue.value)),
];

export default BowtieWizard;
